import { ColumnDef } from "@tanstack/react-table"
import { ChevronDown, ChevronRight, FileText, Folder, Pin } from "lucide-react"

import { StudentAssignmentRowViewModel } from "./viewModels/StudentAssignmentRowViewModel"

interface ColumnOptions {
  onAssignmentClick: (row: StudentAssignmentRowViewModel) => void
}

export function createColumns({
  onAssignmentClick,
}: ColumnOptions): ColumnDef<StudentAssignmentRowViewModel>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: ({ table, column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <button
            className="tw-p-1 hover:tw-bg-gray-100 tw-rounded tw-mr-2"
            onClick={(e) => {
              e.stopPropagation()
              table.toggleAllRowsExpanded()
            }}
            aria-label={
              table.getIsAllRowsExpanded() ? "Collapse all" : "Expand all"
            }
          >
            {table.getIsAllRowsExpanded() ? (
              <ChevronDown className="tw-w-4 tw-h-4" />
            ) : (
              <ChevronRight className="tw-w-4 tw-h-4" />
            )}
          </button>
          Assignment Name
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      cell: ({ row, getValue }) => {
        const rowViewModel = row.original

        return (
          <div
            className="tw-flex tw-items-center tw-gap-2"
            style={{ paddingLeft: `${row.depth * 1.5}rem` }}
          >
            {row.getCanExpand() ? (
              <button
                className="tw-p-1 hover:tw-bg-gray-100 tw-rounded"
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
                aria-label={row.getIsExpanded() ? "Collapse" : "Expand"}
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="tw-w-4 tw-h-4" />
                ) : (
                  <ChevronRight className="tw-w-4 tw-h-4" />
                )}
              </button>
            ) : (
              <div className="tw-w-6" />
            )}

            {rowViewModel.isNewWork && (
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-500 tw-flex-shrink-0" />
            )}

            {(rowViewModel.isFolder || rowViewModel.isInactiveHeader) && (
              <Folder className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
            )}
            {rowViewModel.isAssignment && rowViewModel.isPromoted && (
              <Pin className="tw-w-4 tw-h-4 tw-text-blue-500 tw-flex-shrink-0" />
            )}
            {rowViewModel.isAssignment && !rowViewModel.isPromoted && (
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
            )}

            <span
              className={
                rowViewModel.canClick
                  ? "tw-cursor-pointer hover:tw-underline"
                  : "tw-font-medium"
              }
              onClick={
                rowViewModel.canClick
                  ? (e) => {
                      e.stopPropagation()
                      onAssignmentClick(rowViewModel)
                    }
                  : undefined
              }
            >
              {getValue<string>()}
            </span>
          </div>
        )
      },
      enableSorting: true,
    },
    {
      id: "feedback",
      header: ({ column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Feedback
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      accessorFn: (row) => row.feedbackCount,
      cell: ({ row }) => {
        const rowViewModel = row.original

        if (rowViewModel.feedbackCount === null) {
          return null
        }

        return (
          <span className="tw-text-sm tw-text-gray-500">
            {rowViewModel.feedbackCount}
          </span>
        )
      },
      enableSorting: true,
    },
    {
      id: "points",
      header: ({ column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Points
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      accessorFn: (row) => row.pointsDisplay,
      cell: ({ row }) => {
        const rowViewModel = row.original

        if (!rowViewModel.pointsDisplay) {
          return null
        }

        return (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-font-medium">
              {rowViewModel.pointsDisplay}
            </span>
            {rowViewModel.showNiceIndicator && (
              <span className="tw-text-yellow-500 tw-text-sm">
                &star; Nice!
              </span>
            )}
          </div>
        )
      },
      enableSorting: true,
    },
  ]
}
